import { Link } from "react-router-dom";
import {
  CalendarBlank,
  ClipboardText,
  Confetti,
  ListChecks,
  MapTrifold,
  Palette,
  Users,
  Heart,
  Clock,
  Wine,
  AddressBook,
  WhatsappLogo,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useGet } from "@/hooks/useResource";
import { API_ROOT } from "@/lib/api";
import { Loading, ProgressBar } from "@/components/common";
import { formatEventSchedule, milestoneContent } from "@/lib/milestones";

const HERO = "/assets/gledswood/open-day-hero.jpg";
const WHATSAPP_LINK =
  "https://wa.me/61246541409?text=Hi%20Gledswood%20events%20team%2C%20I%20have%20a%20quick%20question%20about%20our%20wedding%20planning%E2%80%A6";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / 86400000);
}

export default function Dashboard() {
  const { data: event, loading } = useGet("/client/event");
  const { data: summary } = useGet("/client/guests/summary");
  if (loading) return <Loading label="Loading your event" />;

  const days = daysUntil(event?.event_date);
  const eventDate = formatEventSchedule(event?.event_date, event?.event_time);
  const milestones = event?.milestones || [];
  const done = milestones.filter((m) => m.status === "complete").length;
  const next =
    milestones.find((m) => m.status === "in_progress") ||
    milestones.find((m) => m.status === "upcoming");
  const nextContent = milestoneContent(next);
  const heroSrc = event?.has_hero_image
    ? `${API_ROOT}/client/event/hero-image?v=${encodeURIComponent(event.hero_image_updated_at || "")}`
    : event?.hero_image || HERO;

  return (
    <div>
      <p
        className="eyebrow mb-2 animate-fade-up"
        data-testid="dashboard-event-type"
      >
        {event?.event_type || "Your Event"}
      </p>
      <h1
        className="font-heading text-5xl sm:text-6xl font-normal leading-none text-primary break-words animate-fade-up"
        data-testid="dashboard-couple-names"
      >
        {event?.couple_names || "Your Celebration"}
      </h1>
      <p
        className="text-muted-foreground mt-2 animate-fade-up"
        data-testid="dashboard-event-schedule"
      >
        {eventDate}
      </p>

      <div
        className="relative mt-8 overflow-hidden rounded-2xl border border-border animate-fade-up"
        data-testid="countdown-hero"
      >
        <img
          src={heroSrc}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="dashboard-hero-image"
        />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative z-10 flex flex-col items-center gap-6 px-6 py-12 text-center">
          <Confetti
            size={28}
            weight="light"
            className="text-primary-foreground/80"
          />
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-primary-foreground">
            {days > 0
              ? "Days to go"
              : days === 0
                ? "It's today!"
                : "A day to remember"}
          </p>
          {days != null && days >= 0 ? (
            <div
              className="font-heading text-6xl font-light text-primary-foreground leading-none"
              data-testid="countdown-days"
            >
              {days}
            </div>
          ) : days == null ? (
            <p className="font-heading text-3xl text-primary-foreground">
              Date to be confirmed
            </p>
          ) : (
            <p className="font-heading text-3xl text-primary-foreground">
              Congratulations!
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-2xl border border-border bg-card p-6 sm:p-8 animate-fade-up">
          <div>
            <p className="eyebrow">Planning journey</p>
            <h2 className="font-heading text-2xl text-primary mt-1">
              Your progress
            </h2>
          </div>
          <p
            className="font-heading text-4xl text-primary mt-6"
            data-testid="journey-progress"
          >
            {done} of {milestones.length || 13}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Milestones completed
          </p>
          <div className="mt-5">
            <ProgressBar
              value={done}
              max={milestones.length || 13}
              label="Journey complete"
              testid="journey-progress-bar"
            />
          </div>
          <Link
            to="/plan"
            className="inline-flex mt-6 rounded-full border border-border px-4 py-2 text-sm text-primary hover:bg-muted transition-colors duration-200"
            data-testid="view-full-plan-link"
          >
            View all 13 milestones
          </Link>
        </section>
        <section
          className="rounded-2xl border border-accent/30 bg-accent/5 p-6 sm:p-8 animate-fade-up"
          data-testid="next-milestone-card"
        >
          <p className="eyebrow text-accent">Next milestone</p>
          <h2 className="font-heading text-3xl text-primary mt-2">
            {nextContent.label}
          </h2>
          <p className="text-sm text-muted-foreground mt-3">
            {nextContent.description}
          </p>
          <p className="text-xs text-muted-foreground mt-6">
            Your Gledswood team updates journey status as each step is
            confirmed.
          </p>
        </section>
      </div>

      <section className="mt-10" aria-labelledby="quick-actions-heading">
        <p className="eyebrow">Keep planning</p>
        <h2
          id="quick-actions-heading"
          className="font-heading text-3xl text-primary mt-1"
        >
          Quick actions
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <QuickAction
            icon={Heart}
            label="Ceremony"
            detail="Your ceremony details"
            to="/ceremony"
          />
          <QuickAction icon={Clock} label="Running Order" detail="Your day timeline" to="/running-order" />
          <QuickAction
            icon={ListChecks}
            label="Run Sheet"
            detail="Review when issued"
            to="/run-sheet"
          />
          <QuickAction icon={Users} label="Guest List" detail={summary ? `${summary.attending} attending · ${summary.total} total` : "Manage guests"} to="/guests" />
          <QuickAction icon={ClipboardText} label="Menu" detail="Your menu selections" to="/menu" />
          <QuickAction icon={Wine} label="Drinks" detail="Your drink selections" to="/drinks" />
          <QuickAction icon={AddressBook} label="My Suppliers" detail="Your own contacts" to="/suppliers" />
          <QuickAction icon={WhatsappLogo} label="WhatsApp" detail="Message the Events Team" to={WHATSAPP_LINK} external />
          <QuickAction icon={ShieldCheck} label="Social Media" detail="Record your decision" to="/consent" />
        </div>
      </section>
    </div>
  );
}

function QuickAction({ icon: Icon, label, detail, to, external = false }) {
  const content = <><Icon size={23} weight="duotone" className="text-secondary mt-0.5 shrink-0" /><span className="min-w-0"><span className="block font-medium text-primary">{label}</span><span className="block text-sm text-muted-foreground mt-1">{detail}</span></span></>;
  if (external) {
    return <a href={to} data-testid={`quick-action-${label.toLowerCase().replaceAll(" ", "-")}`} className="group flex items-start gap-4 border border-border bg-card p-5 rounded-lg transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg">{content}</a>;
  }
  return (
    <Link
      to={to}
      data-testid={`quick-action-${label.toLowerCase().replaceAll(" ", "-")}`}
      className="group flex items-start gap-4 border border-border bg-card p-5 rounded-lg transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg"
    >
      {content}
    </Link>
  );
}
